import apiClient from '@/api/client';

export interface Mailbox {
  address: string;
  display_name: string;
  can_reply: boolean;
  can_send_as: boolean;
}

export interface Folder {
  name: string;
  label: string;
  special: string | null;
  total: number;
  unread: number;
}

export interface Envelope {
  uid: string;
  subject: string;
  from_name: string;
  from_addr: string;
  date: string | null;
  seen: boolean;
  has_attachments: boolean;
}

export interface Attachment {
  index: number;
  filename: string;
  content_type: string;
  size: number;
}

export interface MessageList {
  total: number;
  limit: number;
  offset: number;
  items: Envelope[];
}

export interface MessageBody {
  uid: string;
  subject: string;
  from: string;
  to: string;
  date: string | null;
  text: string;
  html: string;
  attachments: Attachment[];
  send_as_alias: string | null;
}

export interface ReplyResult {
  status: string;
  to: string;
  message_id: string;
}

const enc = encodeURIComponent;

export async function listMailboxes(): Promise<Mailbox[]> {
  const { data } = await apiClient.get<Mailbox[]>('/mailboxes');
  return data;
}

export async function listFolders(address: string): Promise<Folder[]> {
  const { data } = await apiClient.get<Folder[]>(`/mailboxes/${enc(address)}/folders`);
  return data;
}

export async function listMessages(
  address: string,
  folder = 'INBOX',
  limit = 50,
  offset = 0,
  unreadOnly = false
): Promise<MessageList> {
  const { data } = await apiClient.get<MessageList>(`/mailboxes/${enc(address)}/messages`, {
    params: { folder, limit, offset, unread_only: unreadOnly },
  });
  return data;
}

export async function setSeen(
  address: string,
  uid: string,
  seen: boolean,
  folder = 'INBOX'
): Promise<void> {
  await apiClient.post(
    `/mailboxes/${enc(address)}/messages/${enc(uid)}/flag`,
    { seen },
    { params: { folder } }
  );
}

export async function getMessage(
  address: string,
  uid: string,
  folder = 'INBOX'
): Promise<MessageBody> {
  const { data } = await apiClient.get<MessageBody>(
    `/mailboxes/${enc(address)}/messages/${enc(uid)}`,
    { params: { folder } }
  );
  return data;
}

export interface SearchHit {
  folder: string;
  folder_label: string;
  uid: string;
  subject: string;
  from_name: string;
  from_addr: string;
  date: string | null;
  seen: boolean;
  has_attachments: boolean;
  via_alias: string | null;
}

export interface SearchResponse {
  hits: SearchHit[];
  also_searched: string[];
}

/** Fetch an attachment as a blob (JWT-authenticated) and return an object URL. */
export async function fetchAttachmentUrl(
  address: string,
  uid: string,
  index: number,
  folder = 'INBOX'
): Promise<string> {
  const resp = await apiClient.get(
    `/mailboxes/${enc(address)}/messages/${enc(uid)}/attachments/${index}`,
    { params: { folder }, responseType: 'blob' }
  );
  return URL.createObjectURL(resp.data as Blob);
}

export async function searchMessages(
  address: string,
  q: string,
  fields: { sender: boolean; recipient: boolean; subject: boolean; body: boolean },
  limit = 100
): Promise<SearchResponse> {
  const { data } = await apiClient.get<SearchResponse>(`/mailboxes/${enc(address)}/search`, {
    params: {
      q,
      sender: fields.sender,
      recipient: fields.recipient,
      subject: fields.subject,
      body: fields.body,
      limit,
    },
  });
  return data;
}

export async function moveMessage(
  address: string,
  uid: string,
  destFolder: string,
  folder = 'INBOX'
): Promise<{ status: string; dest_folder: string }> {
  const { data } = await apiClient.post(
    `/mailboxes/${enc(address)}/messages/${enc(uid)}/move`,
    { dest_folder: destFolder },
    { params: { folder } }
  );
  return data;
}

export async function sendReply(
  address: string,
  uid: string,
  text: string,
  folder = 'INBOX',
  to?: string,
  fromAlias?: string
): Promise<ReplyResult> {
  const { data } = await apiClient.post<ReplyResult>(
    `/mailboxes/${enc(address)}/messages/${enc(uid)}/reply`,
    { text, to, from_alias: fromAlias },
    { params: { folder } }
  );
  return data;
}
